<template>
  <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

    <!-- Checkout Result Banner -->
    <div v-if="checkoutResult" class="mb-6 rounded-lg p-4" :class="checkoutResult === 'success' ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'">
      <div class="flex items-center">
        <PhCheckCircle v-if="checkoutResult === 'success'" class="w-5 h-5 text-green-600 mr-2" weight="fill" />
        <PhWarning v-else class="w-5 h-5 text-orange-500 mr-2" weight="fill" />
        <p class="text-sm font-medium" :class="checkoutResult === 'success' ? 'text-green-800' : 'text-orange-800'">
          <template v-if="checkoutResult === 'success'">
            Your subscription is being set up. It may take a moment to activate.
          </template>
          <template v-else-if="checkoutResult === 'bundle-success'">
            Your Course Creator Bundle purchase is being processed.
          </template>
          <template v-else>
            Checkout was canceled. You can try again whenever you're ready.
          </template>
        </p>
      </div>
    </div>

    <!-- Active Subscription Section -->
    <div v-if="sparksStore.hasActiveSubscription && activeSubscription" class="mb-8">
      <!-- Subscription Header Card -->
      <div class="card mb-6">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div class="flex items-center gap-2 mb-1">
              <h2 class="text-xl font-bold text-gray-900">{{ activeSubscription.plan?.name }} Plan</h2>
              <span v-if="activeSubscription.is_founding_member" class="text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                Founding Member
              </span>
            </div>
            <p class="text-gray-600 text-sm">
              Billing period: {{ formatDate(activeSubscription.current_period_start) }} - {{ formatDate(activeSubscription.current_period_end) }}
            </p>
          </div>
          <div class="flex gap-3">
            <button @click="handleManagePortal" :disabled="isLoading" class="btn-secondary text-sm">
              <PhGear class="w-4 h-4 mr-1.5 inline-block" /> Manage Billing
            </button>
            <button v-if="!sparksStore.subscriptionWillCancel" @click="showCancelModal = true" :disabled="isLoading" class="text-sm px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:border-red-300 hover:text-red-600 transition-colors">
              Cancel
            </button>
          </div>
        </div>

        <!-- Cancellation Warning -->
        <div v-if="sparksStore.subscriptionWillCancel" class="mt-4 bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div class="flex items-start">
            <PhWarning class="w-5 h-5 text-orange-500 mr-2 mt-0.5 flex-shrink-0" weight="fill" />
            <div class="flex-1">
              <p class="text-sm font-medium text-orange-800">Subscription Canceling</p>
              <p class="text-sm text-orange-700 mt-1">
                Your subscription will end on {{ formatDate(activeSubscription.current_period_end) }}. You'll keep your allowances until then.
              </p>
              <button @click="reactivateSubscription" :disabled="isLoading" class="mt-2 btn-primary text-sm">
                Reactivate Subscription
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Usage Dashboard -->
      <div v-if="sparksStore.subscriptionUsage.length > 0" class="card mb-6">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">Monthly Usage</h3>
        <div class="space-y-4">
          <div v-for="item in sparksStore.subscriptionUsage" :key="`${item.product_slug}-${item.allowance_type}`" class="flex items-center gap-4">
            <div class="w-40 flex-shrink-0">
              <p class="text-sm font-medium text-gray-900">{{ item.display_name }}</p>
            </div>
            <div class="flex-1">
              <div class="flex items-center gap-3">
                <div class="flex-1 bg-gray-200 rounded-full h-2.5">
                  <div
                    class="h-2.5 rounded-full transition-all duration-500"
                    :class="usageBarColor(item.used, item.monthly_limit)"
                    :style="{ width: `${Math.min((item.used / item.monthly_limit) * 100, 100)}%` }"
                  ></div>
                </div>
                <span class="text-sm font-mono text-gray-600 w-24 text-right">
                  {{ item.used }} / {{ item.monthly_limit }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Flex Sparks Card -->
      <div class="card mb-6">
        <div class="flex items-center justify-between">
          <div>
            <h3 class="text-lg font-semibold text-gray-900">Flex Sparks</h3>
            <p class="text-sm text-gray-600">
              {{ activeSubscription.plan?.sparks_per_month.toLocaleString() }} flex sparks/month (unused sparks roll over, max {{ activeSubscription.plan?.max_rollover.toLocaleString() }})
            </p>
          </div>
          <div class="text-right">
            <p class="text-2xl font-bold font-mono text-gray-900">{{ sparksStore.formattedBalance }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- No Subscription: Show Plans -->
    <div v-else>
      <!-- Header -->
      <div class="text-center mb-8">
        <h1 class="text-3xl font-bold text-gray-900">Subscription Plans</h1>
        <p class="text-gray-600 mt-2">
          Unlock product allowances and flex sparks with a monthly subscription
        </p>
      </div>

      <!-- Founding Member Banner -->
      <div v-if="isFoundingMemberWindow" class="mb-8 bg-primary/5 border border-primary/20 rounded-lg p-6 text-center">
        <p class="text-lg font-semibold text-gray-900 mb-1">Founding Member Pricing</p>
        <p class="text-gray-600 text-sm">
          Subscribe before May 31, 2026 and lock in these prices forever. Prices increase after launch.
        </p>
        <div class="mt-3 flex justify-center gap-6 text-sm">
          <span class="text-gray-500">Future: <span class="line-through">$49</span> / <span class="line-through">$129</span> / <span class="line-through">$349</span></span>
          <span class="font-medium text-primary">Now: $29 / $79 / $199</span>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="plansLoading" class="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
        <div v-for="i in 3" :key="i" class="card animate-pulse">
          <div class="h-6 bg-gray-200 rounded w-1/3 mx-auto mb-4"></div>
          <div class="h-10 bg-gray-200 rounded w-1/2 mx-auto mb-4"></div>
          <div class="space-y-2">
            <div v-for="j in 5" :key="j" class="h-4 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>

      <!-- Plan Cards -->
      <div v-else class="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
        <div
          v-for="plan in sparksStore.plans"
          :key="plan.id"
          class="relative card hover:shadow-lg transition-shadow flex flex-col"
          :class="{ 'ring-2 ring-primary': plan.tier === 'growth' }"
        >
          <!-- Recommended Badge -->
          <div v-if="plan.tier === 'growth'" class="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <span class="bg-primary text-white text-xs font-medium px-3 py-1 rounded-full">Most Popular</span>
          </div>

          <!-- Plan Header -->
          <div class="text-center mb-6">
            <h3 class="text-xl font-semibold text-gray-900">{{ plan.name }}</h3>
            <div class="mt-4">
              <span class="text-4xl font-bold text-gray-900">${{ (plan.price_cents / 100).toFixed(0) }}</span>
              <span class="text-gray-600">/month</span>
            </div>
            <div class="mt-2">
              <span class="text-lg font-bold text-spark font-mono">{{ plan.sparks_per_month.toLocaleString() }}</span>
              <span class="text-gray-600 text-sm"> flex sparks/month</span>
            </div>
          </div>

          <!-- Allowances -->
          <div v-if="plan.allowances?.length" class="mb-4">
            <p class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Monthly Allowances</p>
            <div class="space-y-2">
              <div v-for="allowance in sortedAllowances(plan.allowances)" :key="allowance.id" class="flex items-center text-sm">
                <PhCheck class="w-4 h-4 text-primary mr-2 flex-shrink-0" weight="bold" />
                <span class="text-gray-700">{{ allowance.monthly_limit }} {{ allowance.display_name }}</span>
              </div>
            </div>
          </div>

          <!-- Features -->
          <div class="space-y-2 mb-6">
            <div v-for="feature in plan.features" :key="feature" class="flex items-start text-sm">
              <PhCheck class="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" weight="bold" />
              <span class="text-gray-700">{{ feature }}</span>
            </div>
          </div>

          <!-- Action Button -->
          <div class="mt-auto">
            <button
              @click="handleCheckout(plan)"
              :disabled="isLoading"
              class="w-full px-4 py-2.5 rounded-lg font-medium transition-colors"
              :class="plan.tier === 'growth' ? 'btn-primary' : 'btn-secondary'"
            >
              {{ isLoading ? 'Loading...' : `Choose ${plan.name}` }}
            </button>
          </div>
        </div>
      </div>

      <!-- Course Creator Bundle -->
      <div class="card mb-8 bg-indigo-50/50 border-indigo-100">
        <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 class="text-lg font-semibold text-gray-900">Course Creator Bundle</h3>
            <p class="text-sm text-gray-600 mt-1 mb-3">
              One-time purchase to get started with course creation tools.
            </p>
            <div class="flex flex-wrap gap-3 text-sm text-gray-700">
              <span class="flex items-center"><PhCheck class="w-4 h-4 text-indigo-500 mr-1" weight="bold" /> 1 S2R Conversion</span>
              <span class="flex items-center"><PhCheck class="w-4 h-4 text-indigo-500 mr-1" weight="bold" /> 500 AI Images</span>
              <span class="flex items-center"><PhCheck class="w-4 h-4 text-indigo-500 mr-1" weight="bold" /> 1 Voice Minute</span>
              <span class="flex items-center"><PhCheck class="w-4 h-4 text-indigo-500 mr-1" weight="bold" /> 1 Translation</span>
            </div>
          </div>
          <div class="text-center md:text-right flex-shrink-0">
            <p class="text-3xl font-bold text-gray-900">$29</p>
            <p class="text-xs text-gray-500 mb-2">one-time</p>
            <button @click="handleBundleCheckout" :disabled="isLoading" class="btn-secondary text-sm whitespace-nowrap">
              Buy Bundle
            </button>
          </div>
        </div>
      </div>

      <!-- Benefits Section -->
      <div class="card mb-8 bg-primary/5">
        <h3 class="text-xl font-semibold text-gray-900 mb-4">Subscription Benefits</h3>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div class="text-center">
            <PhArrowsClockwise class="w-8 h-8 mx-auto mb-2 text-primary" weight="fill" />
            <h4 class="font-medium text-gray-900 mb-1">Product Allowances</h4>
            <p class="text-sm text-gray-600">
              Monthly allowances for S2R, images, voice, translations, and more
            </p>
          </div>
          <div class="text-center">
            <PhLightning class="w-8 h-8 mx-auto mb-2 text-spark" weight="fill" />
            <h4 class="font-medium text-gray-900 mb-1">Flex Sparks</h4>
            <p class="text-sm text-gray-600">
              Monthly sparks for anything not covered by allowances. Unused sparks roll over.
            </p>
          </div>
          <div class="text-center">
            <PhShieldCheck class="w-8 h-8 mx-auto mb-2 text-green-500" weight="fill" />
            <h4 class="font-medium text-gray-900 mb-1">30-Day Guarantee</h4>
            <p class="text-sm text-gray-600">
              Build a course or it's free. Full refund within 30 days.
            </p>
          </div>
        </div>
      </div>

      <!-- FAQ -->
      <div>
        <h2 class="text-xl font-semibold text-gray-900 mb-6">Subscription FAQ</h2>
        <div class="space-y-4">
          <div class="card">
            <h4 class="font-medium text-gray-900 mb-2">Can I cancel anytime?</h4>
            <p class="text-gray-600 text-sm">
              Yes, you can cancel your subscription at any time. You'll continue to have access to your allowances until the end of your current billing period.
            </p>
          </div>
          <div class="card">
            <h4 class="font-medium text-gray-900 mb-2">What happens to unused flex sparks?</h4>
            <p class="text-gray-600 text-sm">
              Unused flex sparks automatically roll over to the next month, up to your plan's rollover limit. One-time purchased sparks never expire.
            </p>
          </div>
          <div class="card">
            <h4 class="font-medium text-gray-900 mb-2">What are product allowances?</h4>
            <p class="text-gray-600 text-sm">
              Each plan includes monthly allowances for specific products (e.g., AI image generations, S2R conversions). These reset each billing period. Actions beyond your allowance use flex sparks.
            </p>
          </div>
          <div class="card">
            <h4 class="font-medium text-gray-900 mb-2">Can I change plans?</h4>
            <p class="text-gray-600 text-sm">
              Yes, you can upgrade or downgrade your plan at any time through the Stripe billing portal. Changes take effect at your next billing cycle.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Cancel Confirmation Modal -->
    <div v-if="showCancelModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex min-h-full items-center justify-center p-4">
        <div class="fixed inset-0 bg-black bg-opacity-50" @click="showCancelModal = false"></div>
        <div class="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Cancel Subscription</h3>
          <p class="text-gray-600 mb-6">
            Are you sure you want to cancel your subscription? You'll continue to have access to your allowances until {{ formatDate(activeSubscription?.current_period_end) }}.
          </p>
          <div class="flex space-x-3">
            <button @click="showCancelModal = false" class="flex-1 btn-secondary">
              Keep Subscription
            </button>
            <button
              @click="cancelSubscription"
              :disabled="isLoading"
              class="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors font-medium"
            >
              Cancel Subscription
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import {
  PhCheck,
  PhCheckCircle,
  PhWarning,
  PhGear,
  PhArrowsClockwise,
  PhLightning,
  PhShieldCheck,
} from "@phosphor-icons/vue";
import { useSparksStore } from "../stores/sparks";
import { useNotificationStore } from "../stores/notifications";
import type { SubscriptionPlan } from "../stores/sparks";

const sparksStore = useSparksStore();
const notificationStore = useNotificationStore();
const route = useRoute();

// State
const isLoading = ref(false);
const plansLoading = ref(true);
const showCancelModal = ref(false);

// Founding member window: before May 31, 2026
const isFoundingMemberWindow = computed(() => {
  return new Date() < new Date('2026-05-31T23:59:59Z');
});

// Checkout result from URL query params
const checkoutResult = computed(() => {
  if (route.query.checkout === 'success') return 'success';
  if (route.query.bundle === 'success') return 'bundle-success';
  if (route.query.checkout === 'canceled' || route.query.bundle === 'canceled') return 'canceled';
  return null;
});

// Computed
const activeSubscription = computed(() => sparksStore.activeSubscription);

// Sort allowances by display_order
function sortedAllowances(allowances: SubscriptionPlan['allowances']) {
  if (!allowances) return [];
  return [...allowances].sort((a, b) => a.display_order - b.display_order);
}

// Usage bar color based on consumption
function usageBarColor(used: number, limit: number): string {
  const pct = (used / limit) * 100;
  if (pct >= 90) return 'bg-red-500';
  if (pct >= 70) return 'bg-orange-400';
  return 'bg-primary';
}

// Methods
function formatDate(dateString?: string) {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString();
}

async function handleCheckout(plan: SubscriptionPlan) {
  isLoading.value = true;
  try {
    const result = await sparksStore.createCheckout(plan.stripe_price_id);
    if (result.success && result.checkout_url) {
      window.location.href = result.checkout_url;
    } else {
      notificationStore.error("Checkout Failed", result.error || "Could not create checkout session");
    }
  } catch (error) {
    notificationStore.error("Checkout Failed", "There was an error setting up checkout. Please try again.");
  } finally {
    isLoading.value = false;
  }
}

async function handleBundleCheckout() {
  isLoading.value = true;
  try {
    const BUNDLE_PRICE_ID = 'price_1T1XBFAHYklNlsjgcFEelbn8';
    const result = await sparksStore.createBundleCheckout(BUNDLE_PRICE_ID);
    if (result.success && result.checkout_url) {
      window.location.href = result.checkout_url;
    } else {
      notificationStore.error("Checkout Failed", result.error || "Could not create bundle checkout");
    }
  } catch (error) {
    notificationStore.error("Checkout Failed", "There was an error setting up checkout. Please try again.");
  } finally {
    isLoading.value = false;
  }
}

async function handleManagePortal() {
  isLoading.value = true;
  try {
    const result = await sparksStore.openManagePortal();
    if (result.success && result.portal_url) {
      window.location.href = result.portal_url;
    } else {
      notificationStore.error("Portal Error", result.error || "Could not open billing portal");
    }
  } catch (error) {
    notificationStore.error("Portal Error", "There was an error opening the billing portal.");
  } finally {
    isLoading.value = false;
  }
}

async function cancelSubscription() {
  showCancelModal.value = false;
  isLoading.value = true;

  try {
    const result = await sparksStore.cancelSubscription();
    if (result.success) {
      notificationStore.subscriptionChanged("canceled");
    } else {
      notificationStore.error("Cancellation Failed", result.error);
    }
  } catch (error) {
    notificationStore.error("Cancellation Failed", "There was an error canceling your subscription. Please try again.");
  } finally {
    isLoading.value = false;
  }
}

async function reactivateSubscription() {
  isLoading.value = true;

  try {
    const result = await sparksStore.reactivateSubscription();
    if (result.success) {
      notificationStore.subscriptionChanged("reactivated");
    } else {
      notificationStore.error("Reactivation Failed", result.error);
    }
  } catch (error) {
    notificationStore.error("Reactivation Failed", "There was an error reactivating your subscription. Please try again.");
  } finally {
    isLoading.value = false;
  }
}

async function loadData() {
  plansLoading.value = true;
  try {
    await Promise.all([
      sparksStore.fetchPlans(),
      sparksStore.refreshSubscription(),
      sparksStore.fetchUsage(),
    ]);
  } finally {
    plansLoading.value = false;
  }
}

// Lifecycle
onMounted(async () => {
  if (sparksStore.isInitialized) {
    await loadData();
  } else {
    const unwatch = watch(
      () => sparksStore.isInitialized,
      async (isInitialized) => {
        if (isInitialized) {
          await loadData();
          unwatch();
        }
      }
    );
  }

  // Show success notification from checkout redirect
  if (checkoutResult.value === 'success') {
    notificationStore.subscriptionChanged('created');
  } else if (checkoutResult.value === 'bundle-success') {
    notificationStore.success('Bundle Purchased', 'Your Course Creator Bundle credits have been added.');
  }
});
</script>
